import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import type { MoveDto, RefundSavedDto, SubscriptionClosedDto } from '@tiffinify/contracts';
import { addMonths, daysLabel, formatDay, formatFull, formatMonth, formatPhone, formatShort, mealsOf, orderLineText, waLink, tiffinLines, tiffinsOf, type CalendarCell } from '@tiffinify/core';
import { PageLoader, Badge, BtnSecondary, Card, CardTitle, CalHeader, Empty, ErrorText, Field, FoodMark, Icon, KV, Legend, MealIcon, Meter, MonthGrid, Segmented, TiffinMini, TopBar, LoadError } from '../components/ui';
import { Back, Badge as PBadge, Btn, Btn2, Card as PCard, Ends, Group, Hero as PHero, IB, Ic, Kick, Row as PRow, Seg, Sm, Sp, Top } from '../components/p';
import { FlashBanner } from '../lib/flash';
import { del, post, isNotFound } from '../lib/api';
import { errorMessage, formatInr, mealName, mealsLabel } from '../lib/format';
import { ExtrasCard } from './Extras';
import { useMe, usePersonOrders, useProfile } from '../lib/queries';

const FOOD = { veg: 'Veg', nonveg: 'Non-veg', egg: 'Egg' } as const;

const CAL_CLASS: Record<string, string> = {
  none: '', off: 'off', holiday: 'holiday', delivered: 'done', today: 'today', paused: 'paused', partial: 'partial', upcoming: 'upcoming', end: 'end',
};
/** The calendar's usual day class, plus a marker on days a meal moved away from or arrived on. */
const calClass = (c: CalendarCell) => `${CAL_CLASS[c.state] ?? ''}${c.movedOut.length + c.movedIn.length > 0 ? ' moved' : ''}`;

/** "Dinner, Mon 26 Oct → Lunch, Tue 27 Oct" */
const moveLabel = (m: MoveDto) => `${mealName(m.from.meal)}, ${formatDay(m.from.day)} → ${mealName(m.to.meal)}, ${formatDay(m.to.day)}`;

type ProfileTab = 'plan' | 'meals' | 'money' | 'history';
const TABS: ProfileTab[] = ['plan', 'meals', 'money', 'history'];

export function Profile() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const me = useMe();
  const today = me.data?.today ?? '';
  const [month, setMonth] = useState<string | undefined>(undefined);
  // The section stays in the address, so going back to a profile keeps it.
  const [params, setParams] = useSearchParams();
  const tab: ProfileTab = TABS.includes(params.get('tab') as ProfileTab) ? (params.get('tab') as ProfileTab) : 'plan';
  const pickTab = (t: ProfileTab) => setParams((q) => { const n = new URLSearchParams(q); if (t === 'plan') n.delete('tab'); else n.set('tab', t); return n; }, { replace: true });
  const [moneyPanel, setMoneyPanel] = useState<'close' | 'refund' | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundMethod, setRefundMethod] = useState<'upi' | 'cash'>('upi');
  const [refundDate, setRefundDate] = useState<string | null>(null);
  const q = useProfile(id, month);
  const p = q.data;
  const qc = useQueryClient();
  const flashTo = (text: string, to = `/s/${id}`) => nav(to, { replace: true, state: { flash: { text } } });

  const deleteSub = useMutation({
    mutationFn: () => del<{ ok: boolean; subscriberId: string }>(`/subscriptions/${p!.subscription!.id}`),
    onSuccess: () => {
      void qc.invalidateQueries();
      flashTo('Subscription removed.', p?.earlierPlans.length ? `/s/${id}` : '/subscribers');
    },
  });
  const undo = useMutation({
    mutationFn: (moveId: string) => del<{ newEndDate: string }>(`/subscriptions/${p!.subscription!.id}/moves/${moveId}`),
    onSuccess: () => void qc.invalidateQueries(),
  });
  const amountToRefund = Number(refundAmount.replace(/\D/g, '')) || 0;
  const refundBody = () => ({ amount: amountToRefund, method: refundMethod, refundedOn: refundDate ?? today });
  const close = useMutation({
    mutationFn: () => post<SubscriptionClosedDto>(`/subscriptions/${p!.subscription!.id}/close`, { refund: amountToRefund > 0 ? refundBody() : undefined }),
    onSuccess: (res) => {
      void qc.invalidateQueries();
      setMoneyPanel(null);
      const refunded = res.refund ? ` ${formatInr(res.refund.amount)} refund recorded.` : '';
      const due = res.state.due > 0 ? ` ${formatInr(res.state.due)} is still due.` : '';
      flashTo(`Subscription closed: no more meals on the kitchen list.${refunded}${due}`);
    },
  });
  const refund = useMutation({
    mutationFn: () => post<RefundSavedDto>(`/subscriptions/${p!.subscription!.id}/refunds`, refundBody()),
    onSuccess: (res) => {
      void qc.invalidateQueries();
      setMoneyPanel(null);
      setRefundAmount('');
      flashTo(`${formatInr(res.refund.amount)} refund recorded. ${formatInr(res.state.paid)} kept${res.state.due > 0 ? ` · ${formatInr(res.state.due)} due` : ''}.`);
    },
  });

  // Follow the server's month once loaded so prev/next start from what is on screen.
  const shown = p?.calendar?.month ?? month;
  useEffect(() => { window.scrollTo(0, 0); }, [id]);

  if (q.isPending) return <main className="main task"><TopBar to="/subscribers" label="Back to subscribers" /><PageLoader label="Opening the card" /></main>;
  if (q.isError && !isNotFound(q.error)) return <main className="main task"><TopBar to="/subscribers" label="Back to subscribers" /><LoadError what="this customer" onRetry={() => void q.refetch()} /></main>;
  if (!p) return <main className="main task"><TopBar to="/subscribers" label="Back to subscribers" /><Empty title="Customer not found">They may have been deleted.</Empty></main>;

  const { subscriber: s, subscription: sub, summary: sm, payment: pay } = p;
  const perDay = sub ? mealsOf(sub.slot).length : 1;
  const doneDays = sm && sub ? Math.min(sub.mealDays, Math.round(sm.delivered / perDay)) : 0;
  const state = sm?.state;
  const finished = state === 'ended' || state === 'closed';
  const refundTooHigh = amountToRefund > (pay?.paid ?? 0);
  const cancelPanel = () => { setMoneyPanel(null); setRefundAmount(''); };

  return (
    <main className="main task">
      <Top>
        <Back to="/subscribers" label="Subscribers" />
        <Sp />
        <IB icon="edit" label={`Edit ${s.name.split(' ')[0]}’s details`} to={`/s/${s.id}/edit`} />
      </Top>
      <FlashBanner />

      <div className="p-stack profile-name">
        <div className="p-tline">
          <h1 className="p-h1">{s.name}</h1>
          {state === 'active' ? <PBadge tone="g">Active</PBadge>
            : state === 'upcoming' && sub ? <PBadge>Starts {formatShort(sub.startDate)}</PBadge>
            : finished ? <PBadge tone="r">{state === 'closed' ? 'Closed' : 'Ended'}</PBadge>
            : null}
        </div>
        <span className="p-sub">{[sub ? mealsLabel(sub.slot) : null, sub?.days ? daysLabel(sub.days) : null, s.area || 'No area'].filter(Boolean).join(' · ')}</span>
        <span className="p-sub profile-line"><FoodMark food={s.food} />{FOOD[s.food]} · {formatPhone(s.phone)}{s.email ? ` · ${s.email}` : ''}</span>
        {s.notes && <span className="p-sub">“{s.notes}”</span>}
      </div>

      {sub && sm && pay ? (
        <>
          {tab === 'plan' && <PHero style={{ padding: '18px 20px', flexDirection: 'row', justifyContent: 'space-between', gap: 12 }} label="Plan summary">
            {state === 'closed' ? (
              <div><Kick>Delivered</Kick><div className="p-mid" style={{ marginTop: 6 }}>{doneDays}</div></div>
            ) : (
              <div><Kick>Meals left</Kick><div className="p-mid" style={{ marginTop: 6 }}>{sm.left}</div></div>
            )}
            <div><Kick>{state === 'closed' ? 'Closed on' : 'Ends'}</Kick><b style={{ display: 'block', fontSize: 16, marginTop: 10 }}>{formatShort(state === 'closed' ? sub.closedOn ?? today : sm.endDate)}</b>{state !== 'closed' && sm.pausedDays > 0 && <small style={{ color: '#D9A54A', fontSize: 12 }}>+{sm.pausedDays} paused</small>}</div>
            <div><Kick>Due</Kick><b style={{ display: 'block', fontSize: 16, marginTop: 10, color: pay.due > 0 ? '#F2B544' : '#F5EFE6' }}>{pay.due > 0 ? <Link to={`/pay/${sub.id}`} aria-label="Record payment" style={{ color: 'inherit' }}>{formatInr(pay.due)}</Link> : formatInr(pay.due)}</b></div>
          </PHero>}

          {tab === 'plan' && (finished ? (
            <div className="p-stack" style={{ gap: 10 }}>
              <Btn label="Renew plan" go="refresh" to={`/s/${s.id}/renew`} />
              <Btn2 label="Send status" icon="chat" to={`/remind/${sub.id}?type=status`} />
              <Btn2 label="Send their My plan page" icon="link" to={`/s/${s.id}/plan-link`} />
            </div>
          ) : (
            <nav className="p-qa" aria-label="Plan actions">
              <Link to={`/s/${s.id}/pause`} aria-label="Pause days"><span><Icon name="pause" size={18} sw={2.2} /></span>Pause</Link>
              <Link to={`/s/${s.id}/move`} aria-label="Move a meal"><span><Icon name="swap" size={18} sw={1.9} /></span>Move</Link>
              <Link to={`/s/${s.id}/renew`} aria-label="Renew plan"><span><Icon name="refresh" size={18} sw={1.9} /></span>Renew</Link>
              <Link to={`/remind/${sub.id}?type=status`} aria-label="Send status"><span><Icon name="chat" size={18} sw={1.9} /></span>Message</Link>
              <Link to={`/s/${s.id}/plan-link`} aria-label="My plan page"><span><Icon name="link" size={18} sw={1.9} /></span>My plan</Link>
            </nav>
          ))}

          <Seg label="Profile sections" value={tab} onChange={pickTab}
            options={[{ value: 'plan', label: 'Plan' }, { value: 'meals', label: 'Meals' }, { value: 'money', label: 'Money' }, { value: 'history', label: 'History' }]} />

          {tab === 'plan' && <>
          {p.calendar && shown && (
            <Card pad="sm">
              <CalHeader title={formatMonth(shown)} onPrev={() => setMonth(addMonths(shown, -1))} onNext={() => setMonth(addMonths(shown, 1))} />
              <MonthGrid cells={p.calendar.cells} aria={`${formatMonth(shown)} meal calendar`} classFor={calClass} />
              <Legend items={[
                { cls: 'done', label: 'Delivered' }, { cls: 'paused', label: 'Paused' }, { cls: '', label: 'Coming up' }, { cls: 'end', label: 'Ends' },
                ...(p.moves.length > 0 ? [{ cls: 'moved', label: 'Moved' }] : []),
              ]} />
              {sm.pausedDays > 0 && (
                <p className="helper"><Icon name="pause" size={16} /><span>{sm.pausedDays} paused meal days added to the end. Days off the plan and holidays don’t count.</span></p>
              )}
            </Card>
          )}

          {p.moves.length > 0 && (
            <Card pad>
              <CardTitle title="Moved meals" />
              <div>
                {p.moves.map((m) => (
                  <KV key={m.id} k={moveLabel(m)} v={m.canUndo ? (
                    <button type="button" className="text-link undo" disabled={undo.isPending} aria-label={`Undo: ${moveLabel(m)}`} onClick={() => undo.mutate(m.id)}>
                      {undo.isPending && undo.variables === m.id ? <TiffinMini /> : 'Undo'}
                    </button>
                  ) : ''} />
                ))}
              </div>
              <ErrorText>{undo.error ? errorMessage(undo.error) : ''}</ErrorText>
            </Card>
          )}

          </>}

          {tab === 'meals' && <>
          <Card pad>
            <CardTitle title="Meal choices" right={!finished && <Link to={`/s/${s.id}/edit`} className="text-link">Edit choices</Link>} />
            <div className="meal-lines">
              {mealsOf(sub.slot).map((m) => (
                <div key={m} className="meal-line">
                  <span className="meal-line-meal"><MealIcon meal={m} size={15} />{mealName(m)}</span>
                  <span className="meal-line-tiffins">
                    {tiffinLines(tiffinsOf(sub.mealRules?.[m], s.food)).map((l) => (
                      <span key={`${l.food}:${l.name}`} className="meal-line-tiffin">
                        <FoodMark food={l.food} />
                        <span className="meal-line-name">
                          {l.quantity > 1 ? `${l.quantity}× ` : ''}{l.name}
                          {l.custom && <small className="meal-line-kind">{l.pool ? 'Rotates daily' : 'Fixed'}</small>}
                        </span>
                      </span>
                    ))}
                  </span>
                </div>
              ))}
            </div>
          </Card>

          </>}

          {tab === 'money' && <>
          <Card pad>
            <CardTitle title="Payments" right={pay.due > 0 ? <Badge kind="due">{formatInr(pay.due)} due</Badge> : state === 'closed' ? <Badge kind="end">Closed</Badge> : <Badge kind="ok" icon="check">Paid up</Badge>} />
            <div className="stack">
              <div className="pay-total"><b>{formatInr(pay.paid)}</b><span>{pay.refunded > 0 ? `kept, after ${formatInr(pay.refunded)} refunded` : `paid of ${formatInr(pay.price)}`}</span></div>
              <Meter value={pay.paid} total={pay.price} />
            </div>
            {p.payments.length === 0 ? (
              <p className="pay-none">No payments yet.</p>
            ) : (
              <div className="pay-list">
                {[...p.payments].reverse().map((x) => (
                  <div key={x.id} className="pay-row">
                    <span className={`pay-method-badge ${x.kind === 'refund' ? 'refund' : x.method}`}>{x.kind === 'refund' ? 'Refund' : x.method === 'upi' ? 'UPI' : 'Cash'}</span>
                    <span className="grow">{formatDay(x.paidOn)}</span>
                    <b className={x.kind === 'refund' ? 'refund' : undefined}>{x.kind === 'refund' ? '−' : ''}{formatInr(x.amount)}</b>
                  </div>
                ))}
              </div>
            )}
            {finished && !moneyPanel && pay.paid > 0 && (
              <div className="pay-actions"><BtnSecondary label="Record a refund" icon="rupee" sm onClick={() => { setMoneyPanel('refund'); setRefundAmount(''); }} /></div>
            )}
            {pay.due > 0 && (
              <div className="pay-actions">
                <BtnSecondary label="Record payment" icon="rupee" to={`/pay/${sub.id}`} sm ink />
                <BtnSecondary label="Remind" icon="chat" to={`/remind/${sub.id}?type=payment`} sm />
              </div>
            )}
          </Card>

          <ExtrasCard
            subscriptionId={sub.id}
            name={s.name}
            extras={p.extras ?? []}
            served={me.data?.kitchen.meals ?? sub.slot}
            today={today}
            lastDay={sm.endDate}
            canAdd={!finished && sm.endDate >= today}
          />

          {confirmDelete ? (
            <Card pad>
              <CardTitle title="Cancel this subscription" />
              <p className="sub">No meals have gone out yet, so it can go completely, with all its future meals.</p>
              <ErrorText>{deleteSub.error ? errorMessage(deleteSub.error) : ''}</ErrorText>
              <div className="pay-actions">
                <BtnSecondary label={deleteSub.isPending ? 'Cancelling…' : 'Yes, cancel it'} icon="close" danger busy={deleteSub.isPending} onClick={() => deleteSub.mutate()} />
                <BtnSecondary label="Keep it" onClick={() => setConfirmDelete(false)} />
              </div>
            </Card>
          ) : moneyPanel ? (
            <Card pad>
              <CardTitle title={moneyPanel === 'close' ? 'Close subscription' : 'Record a refund'} />
              {moneyPanel === 'close' && <p className="sub">Meals stop now: {s.name.split(' ')[0]} comes off today’s and every later kitchen list. A refund here lowers what they have paid.</p>}
              <Field label="Refund amount" id="refund-amount" value={refundAmount} onChange={(v) => setRefundAmount(v.replace(/\D/g, ''))} prefix="₹" inputMode="numeric" hint={`Up to ${formatInr(pay.paid)}`} error={refundTooHigh ? `Only ${formatInr(pay.paid)} has been paid and not refunded` : undefined} />
              {amountToRefund > 0 && (
                <>
                  <div className="stack"><span className="label">Refund by</span><Segmented label="Refund by" value={refundMethod} onChange={setRefundMethod} options={[{ value: 'upi', label: 'UPI' }, { value: 'cash', label: 'Cash' }]} /></div>
                  <Field label="Refund date" id="refund-date" type="date" value={refundDate ?? today} onChange={setRefundDate} />
                </>
              )}
              <ErrorText>{close.error ? errorMessage(close.error) : refund.error ? errorMessage(refund.error) : ''}</ErrorText>
              <div className="pay-actions">
                {moneyPanel === 'close' ? (
                  <BtnSecondary label={close.isPending ? 'Closing…' : amountToRefund > 0 ? `Refund ${formatInr(amountToRefund)} and close` : 'Close without a refund'} icon="close" danger busy={close.isPending} disabled={refundTooHigh} onClick={() => close.mutate()} />
                ) : (
                  <BtnSecondary label={refund.isPending ? 'Saving…' : `Record ${formatInr(amountToRefund)} refund`} icon="check" busy={refund.isPending} disabled={amountToRefund === 0 || refundTooHigh} onClick={() => refund.mutate()} />
                )}
                <BtnSecondary label="Cancel" onClick={cancelPanel} />
              </div>
            </Card>
          ) : !finished && (
            <div className="card-actions-responsive">
              <BtnSecondary label="Close subscription" icon="close" danger onClick={() => setMoneyPanel('close')} />
              {doneDays === 0 && <BtnSecondary label="Cancel and delete" icon="close" danger onClick={() => setConfirmDelete(true)} />}
            </div>
          )}

          </>}

          {tab === 'history' && (p.earlierPlans.length === 0
            ? <Card><Empty title="No earlier plans">Earlier plans show here once this one is renewed.</Empty></Card>
            : <>
          {p.earlierPlans.length > 0 && (
            <Card pad>
              <CardTitle title="Earlier plans" />
              <div>{p.earlierPlans.map((e) => <KV key={e.id} k={e.planName} v={`${formatShort(e.startDate)} – ${formatFull(e.endDate)}`} />)}</div>
            </Card>
          )}
            </>)}
          {tab === 'history' && <PersonOrders subscriberId={s.id} />}
        </>
      ) : (
        <>
          <PCard><PRow left={<Ic icon="calendar" tone="s" />} t="Offer a monthly plan" s="Use this customer’s details to start a subscription."
            right={<Sm label="Offer" k to={`/s/${s.id}/renew`} />} /></PCard>
          <PersonOrders subscriberId={s.id} />
          <nav className="p-qa" aria-label="Customer actions" style={{gridTemplateColumns: 'repeat(3, minmax(0, 1fr))'}}>
            <Link to="/orders/new" state={{customer: {id: s.id, name: s.name, phone: s.phone, area: s.area}}}><span><Icon name="plus" /></span>New order</Link>
            <a href={waLink(s.phone, '')}><span><Icon name="chat" /></span>Message</a>
            <a href={`tel:${s.phone}`}><span><Icon name="phone" /></span>Call</a>
          </nav>
        </>
      )}
    </main>
  );
}

/** Orders and extras booked one-off by this person, newest first. */
function PersonOrders({ subscriberId }: { subscriberId: string }) {
  const orders = usePersonOrders(subscriberId).data?.orders ?? [];
  if (orders.length === 0) return null;
  return (
    <Group label="Orders">
      <PCard>
        {orders.map((o) => (
          <PRow key={o.id} to={`/orders/${o.id}`} t={`#${o.number} · ${formatShort(o.day)}`} s={o.lines.map(orderLineText).join(' · ')}
            right={<Ends amt={formatInr(o.total)} badge={o.status === 'cancelled' ? <PBadge>Cancelled</PBadge> : <PBadge tone={o.payState === 'paid' ? 'g' : o.payState === 'says_paid' ? 's' : 'r'}>{o.payState === 'paid' ? 'Paid' : o.payState === 'says_paid' ? 'Says paid' : 'Unpaid'}</PBadge>} />} />
        ))}
      </PCard>
    </Group>
  );
}
